import { useCallback, useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  useRequestWithFeedback,
  combineRunProps,
  type LayeredOverrideRunProps,
  type OverrideRunProps,
} from "../../../composites";
import { useObjectMemo } from "../../../hooks";

import { API } from "./api";
import { actions } from "./slice";
import { selectWireRecipients } from "./selectors";

import type { BootstrapFetchRequest } from "../../bootstrap";

export const useFetchRecentWires = () => {
  const dispatch = useDispatch();
  const { send, loading } = useRequestWithFeedback<API.FetchWires.Response>();

  const fetchWires = useCallback(
    (
      accountId: API.AccountId,
      overrideRunProps: OverrideRunProps<API.FetchWires.Response> = {},
    ) => {
      send({
        action: API.fetchRecent(accountId),
        onData: (data) => {
          dispatch(actions.receive(accountId, data));
        },
        ...overrideRunProps,
      });
    },
    [dispatch, send],
  );
  return useObjectMemo({ send: fetchWires, loading });
};

export const useFetchWireRecipients = () => {
  const dispatch = useDispatch();
  const { send, loading, status } =
    useRequestWithFeedback<API.FetchWireRecipients.Response>();
  const [nextPage, setNextPage] = useState<number | null>(null);
  const inFlightRef = useRef(false);
  const overrideRunPropsRef = useRef<
    OverrideRunProps<API.FetchWireRecipients.Response>
  >({});

  const fetchPage = useCallback(
    (page: number) => {
      send({
        action: API.listWireRecipients(page),
        ...combineRunProps(
          {
            onData: (data) => {
              dispatch(actions.receiveWireRecipients(data));
              if (data.next) {
                setNextPage(page + 1);
              } else {
                inFlightRef.current = false;
                setNextPage(null);
              }
            },
            onError: () => {
              inFlightRef.current = false;
              setNextPage(null);
            },
          },
          overrideRunPropsRef.current,
        ),
      });
    },
    [dispatch, send],
  );

  const fetchWireRecipients = useCallback(
    (
      overrideRunProps: OverrideRunProps<API.FetchWireRecipients.Response> = {},
    ) => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      overrideRunPropsRef.current = overrideRunProps;
      fetchPage(1);
    },
    [fetchPage],
  );

  useEffect(() => {
    if (nextPage === null || loading) {
      return;
    }
    fetchPage(nextPage);
  }, [nextPage, loading, fetchPage]);

  const hasData = useSelector(selectWireRecipients).length > 0;

  return useObjectMemo({
    send: fetchWireRecipients,
    loading: loading || nextPage !== null,
    error: status === "error",
    hasData,
  }) satisfies BootstrapFetchRequest;
};

export const useDeleteWireRecipient = () => {
  const dispatch = useDispatch();
  const { send } = useRequestWithFeedback<API.FetchWireRecipients.Response>();

  const deleteWireRecipient = useCallback(
    (
      id: API.WireRecipientId,
      overrideRunProps: OverrideRunProps<API.FetchWireRecipients.Response> = {},
    ) => {
      send({
        action: API.deleteWireRecipient(id),
        onData: () => {
          dispatch(actions.deleteWireRecipient(id));
        },
        messaging: {
          toast: {
            success: "Recipient successfully removed.",
            error: "Recipient could not be removed. Please try again.",
          },
        },
        ...overrideRunProps,
      });
    },
    [dispatch, send],
  );
  return useObjectMemo({ send: deleteWireRecipient });
};

export const useAddWireRecipientRequest = () => {
  const dispatch = useDispatch();
  const { send } = useRequestWithFeedback<
    API.CreateOrUpdateWireRecipient.Response,
    API.CreateOrUpdateWireRecipient.Error
  >();
  const addWireRecipient = useCallback(
    (
      wireRecipient: API.CreateOrUpdateWireRecipient.Request,
      overrideRunProps: LayeredOverrideRunProps<
        API.CreateOrUpdateWireRecipient.Response,
        API.CreateOrUpdateWireRecipient.Error
      > = {},
    ) => {
      send({
        action: API.saveWireRecipient(wireRecipient),
        ...combineRunProps<
          API.CreateOrUpdateWireRecipient.Response,
          API.CreateOrUpdateWireRecipient.Error
        >(
          {
            onData: (data) => {
              dispatch(actions.createWireRecipient(data));
            },
          },
          overrideRunProps,
        ),
      });
    },
    [send, dispatch],
  );

  return useObjectMemo({ send: addWireRecipient });
};
